import React, { useState } from 'react'
import {
  Box, Button, TextField, Paper, FormControl, InputLabel, Select, MenuItem, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Divider,
  Popover,
} from '@mui/material'
import { Grid } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { router, usePage, useForm } from '@inertiajs/react'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'

Form.layout = (page) => <AppShell>{page}</AppShell>

export default function Form({
  property, budget_plan, fiscal_years = [], cost_categories = [], distribution_keys = [],
  cost_category_key_mappings = {}, existing_line_items = [], previous_plans = [],
  action, errors: serverErrors = {},
}) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const isEdit = action === 'edit'
  const basePath = `/m/${tenant.slug}/properties/${property.id}`
  const title = property.management_type === 'weg' ? 'Wirtschaftsplan' : 'Budgetplan'

  const { data, setData, post, put, processing, errors } = useForm({
    name: budget_plan.name || '',
    fiscal_year_id: budget_plan.fiscal_year_id || '',
    rounding_mode: budget_plan.rounding_mode || 'euro',
  })

  const [lineItems, setLineItems] = useState(
    existing_line_items.length > 0
      ? existing_line_items.map((li) => ({
          cost_category_id: li.cost_category_id || '',
          distribution_key_id: li.distribution_key_id || '',
          amount: li.amount || '',
        }))
      : [{ cost_category_id: '', distribution_key_id: '', amount: '' }]
  )

  const [selectedPreviousPlan, setSelectedPreviousPlan] = useState('')
  const [copyProcessing, setCopyProcessing] = useState(false)
  const [warningAnchor, setWarningAnchor] = useState(null)
  const [warningIndex, setWarningIndex] = useState(null)

  const allErrors = { ...serverErrors, ...errors }

  const handleCopyFromPrevious = () => {
    if (!selectedPreviousPlan || !data.fiscal_year_id) return
    setCopyProcessing(true)
    router.post(`${basePath}/budget_plans/create_from_previous`, {
      previous_plan_id: selectedPreviousPlan,
      fiscal_year_id: data.fiscal_year_id,
    })
  }

  const addLineItem = () => {
    setLineItems([...lineItems, { cost_category_id: '', distribution_key_id: '', amount: '' }])
  }

  const removeLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index))
  }

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems]
    updated[index] = { ...updated[index], [field]: value }
    if (field === 'cost_category_id') {
      const defaultDkId = cost_category_key_mappings[value]
      updated[index].distribution_key_id = defaultDkId || ''
    }
    setLineItems(updated)
  }

  const getDefaultDkId = (costCategoryId) => {
    return cost_category_key_mappings[costCategoryId] || null
  }

  const isDkDiverging = (li) => {
    const defaultDkId = getDefaultDkId(li.cost_category_id)
    if (!defaultDkId || !li.cost_category_id) return false
    return li.distribution_key_id !== '' && li.distribution_key_id !== defaultDkId
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const payload = {
      budget_plan: {
        name: data.name,
        fiscal_year_id: data.fiscal_year_id,
        rounding_mode: data.rounding_mode,
      },
      line_items: lineItems.filter((li) => li.cost_category_id && li.amount),
    }
    if (isEdit) {
      router.put(`${basePath}/budget_plans/${budget_plan.id}`, payload)
    } else {
      router.post(`${basePath}/budget_plans`, payload)
    }
  }

  return (
    <Box>
      <PageHeader
        title={isEdit ? `${title} bearbeiten` : `Neuer ${title}`}
        breadcrumbs={[
          { label: property.name, href: basePath },
          { label: `${title}e`, href: `${basePath}/budget_plans` },
          { label: isEdit ? 'Bearbeiten' : 'Neu' },
        ]}
      />

      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', maxWidth: 900 }}>
        <Box component="form" onSubmit={handleSubmit} data-testid="budget-plan-form">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField label="Name" required fullWidth size="small"
                value={data.name} onChange={(e) => setData('name', e.target.value)}
                error={!!allErrors.name} helperText={allErrors.name?.[0]}
                data-testid="name-input" />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth size="small" required error={!!allErrors.fiscal_year_id}>
                <InputLabel>Geschäftsjahr</InputLabel>
                <Select value={data.fiscal_year_id} label="Geschäftsjahr"
                  onChange={(e) => setData('fiscal_year_id', e.target.value)}
                  data-testid="fiscal-year-select">
                  {fiscal_years.map((fy) => (
                    <MenuItem key={fy.id} value={fy.id}>{fy.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Rundung</InputLabel>
                <Select value={data.rounding_mode} label="Rundung"
                  onChange={(e) => setData('rounding_mode', e.target.value)}
                  data-testid="rounding-select">
                  <MenuItem value="cent">Cent</MenuItem>
                  <MenuItem value="ten_cent">10 Cent</MenuItem>
                  <MenuItem value="euro">Euro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Copy from previous plan */}
          {!isEdit && previous_plans.length > 0 && (
            <Box sx={{ mt: 3 }} data-testid="copy-from-previous-section">
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Aus Vorjahr erstellen
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Alle Positionen aus einem bestehenden Plan übernehmen.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <FormControl size="small" sx={{ minWidth: 300 }}>
                  <InputLabel>Vorjahresplan</InputLabel>
                  <Select
                    value={selectedPreviousPlan}
                    label="Vorjahresplan"
                    onChange={(e) => setSelectedPreviousPlan(e.target.value)}
                    data-testid="previous-plan-select"
                  >
                    {previous_plans.map((bp) => (
                      <MenuItem key={bp.id} value={bp.id}>{bp.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyFromPrevious}
                  disabled={!selectedPreviousPlan || !data.fiscal_year_id || copyProcessing}
                  data-testid="copy-from-previous-btn"
                >
                  {copyProcessing ? 'Wird erstellt...' : 'Übernehmen'}
                </Button>
              </Box>
              <Divider sx={{ mt: 2 }} />
            </Box>
          )}

          {/* Line Items */}
          <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            Kostenpositionen
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Kostenkategorie</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Verteilerschlüssel</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Jahresbetrag (€)</TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((li, index) => (
                  <TableRow key={index} data-testid={`line-item-row-${index}`}>
                    <TableCell sx={{ p: 0.5 }}>
                      <FormControl fullWidth size="small">
                        <Select value={li.cost_category_id} displayEmpty
                          onChange={(e) => updateLineItem(index, 'cost_category_id', e.target.value)}
                          data-testid={`line-item-category-${index}`}>
                          <MenuItem value="" disabled>Kategorie wählen</MenuItem>
                          {cost_categories.map((cc) => (
                            <MenuItem key={cc.id} value={cc.id}>{cc.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FormControl fullWidth size="small">
                          <Select value={li.distribution_key_id} displayEmpty
                            onChange={(e) => updateLineItem(index, 'distribution_key_id', e.target.value)}
                            data-testid={`line-item-dk-${index}`}>
                            <MenuItem value="">–</MenuItem>
                            {distribution_keys.map((dk) => (
                              <MenuItem key={dk.id} value={dk.id}>{dk.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {isDkDiverging(li) && (
                          <IconButton
                            size="small"
                            onClick={(e) => { setWarningAnchor(e.currentTarget); setWarningIndex(index) }}
                            data-testid={`dk-warning-${index}`}
                            sx={{ color: 'warning.main', p: 0.5 }}
                          >
                            <WarningAmberIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }}>
                      <TextField fullWidth size="small" type="number"
                        value={li.amount}
                        onChange={(e) => updateLineItem(index, 'amount', e.target.value)}
                        slotProps={{ htmlInput: { min: 0, step: '0.01', 'data-testid': `line-item-amount-${index}` } }}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }}>
                      <IconButton size="small" onClick={() => removeLineItem(index)}
                        data-testid={`remove-line-item-${index}`}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button size="small" startIcon={<AddIcon />} onClick={addLineItem}
            sx={{ mt: 1 }} data-testid="add-line-item-btn">
            Position hinzufügen
          </Button>

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button type="submit" variant="contained" disabled={processing} data-testid="submit-btn">
              {processing ? 'Wird gespeichert...' : 'Speichern'}
            </Button>
            <Button variant="outlined" onClick={() => router.visit(`${basePath}/budget_plans`)}>Abbrechen</Button>
          </Box>
        </Box>
      </Paper>

      <Popover
        open={warningAnchor !== null}
        anchorEl={warningAnchor}
        onClose={() => { setWarningAnchor(null); setWarningIndex(null) }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        data-testid="dk-warning-popover"
      >
        <Box sx={{ p: 1.5, maxWidth: 300 }}>
          <Typography variant="body2" color="warning.main" data-testid="dk-warning-text">
            Verteilerschlüssel weicht vom Standard ab
          </Typography>
        </Box>
      </Popover>
    </Box>
  )
}
